import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { sortBy } from '@sorare/core/src/lib/arrays';

import { CollectionAnimationClub } from '../CollectionAnimationClub';
import { CollectionAnimationHeader } from '../CollectionAnimationHeader';
import { collectionAnimationConfig } from '../animationConfig';
import type {
  CollectionAnimationCompetitionQuery,
  CollectionAnimationCompetitionQueryVariables,
  CollectionAnimationCompetition_anyCardInterface,
  CollectionAnimationCompetition_competition,
} from './__generated__/index.graphql';
import { useAnimatingCardIndex } from './useAnimatingCardIndex';
import { useDisplayedCollections } from './useDisplayedCollections';
import { useTranslateY } from './useTranslateY';

const Root = styled(Vertical)`
  width: 100%;
  min-width: 100%;
  background-color: var(--c-black);
  position: relative;
  height: 100%;
`;

const CollectionsWrapper = styled(Vertical)`
  transition: transform ${collectionAnimationConfig.scrollDurationMs}ms
    cubic-bezier(0.02, 0.36, 0.34, 0.97);
  flex: 1;
`;

const COLLECTION_ANIMATION_COMPETITION_QUERY = gql`
  query CollectionAnimationCompetitionQuery(
    $user: String!
    $rarities: [Rarity!]
    $competitionSlug: String!
    $seasonStartYear: Int!
  ) {
    currentUser {
      slug
      cardCollections(
        forCompetitions: [$competitionSlug]
        seasonStartYears: [$seasonStartYear]
        rarities: $rarities
        first: 50
      ) {
        nodes {
          slug
          userCardCollection(forUserSlug: $user) {
            id
            slots {
              slug
              highlightedCardCollectionCard {
                id
                anyCard {
                  slug
                }
              }
            }
          }
          anyTeam {
            slug
            shortName
          }
          ...CollectionAnimationHeader_cardCollection
          ...CollectionAnimationClub_cardCollection
        }
      }
    }
  }
  ${CollectionAnimationHeader.fragments.cardCollection}
  ${CollectionAnimationClub.fragments.cardCollection}
` as TypedDocumentNode<
  CollectionAnimationCompetitionQuery,
  CollectionAnimationCompetitionQueryVariables
>;

type Props = {
  competition: CollectionAnimationCompetition_competition;
  newCards: CollectionAnimationCompetition_anyCardInterface[];
  shouldAnimate: boolean;
  onAnimationEnd: () => void;
};

export const CollectionAnimationCompetition = ({
  competition,
  newCards,
  shouldAnimate,
  onAnimationEnd,
}: Props) => {
  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const { data, loading } = useQuery(COLLECTION_ANIMATION_COMPETITION_QUERY, {
    variables: {
      user: contextCurrentUser?.slug || '',
      rarities: [Rarity.common],
      competitionSlug: competition.slug,
      seasonStartYear: newCards.at(0)?.seasonYear as number,
    },
    skip: !contextCurrentUser?.slug || !newCards.at(0)?.seasonYear,
  });
  const cardCollections = data?.currentUser?.cardCollections?.nodes || [];

  const displayedCardCollections = useDisplayedCollections({
    cardCollections,
    newCards,
  });
  const sortedNewCards = sortBy(
    card => card.anyTeam?.shortName || '',
    [...newCards]
  );
  const { translateY, scrollTo, clubRefCallback } = useTranslateY({
    sortedNewCards,
    displayedCardCollections,
  });
  const {
    animatingCardIndex,
    setAnimatingCardIndex,
    setAnimatingCardIndexAfterScroll,
  } = useAnimatingCardIndex({ shouldAnimate: shouldAnimate && !loading });

  if (loading || displayedCardCollections.length === 0) {
    return null;
  }

  return (
    <Root>
      <CollectionAnimationHeader
        cardCollections={cardCollections}
        competition={competition}
        newCardSlugs={sortedNewCards.map(card => card.slug)}
        animatingCardIndex={animatingCardIndex}
      />
      <CollectionsWrapper
        style={
          {
            transform: `translateY(${translateY}px)`,
          } as CSSProperties
        }
      >
        {displayedCardCollections.map((cardCollection, index) => (
          <CollectionAnimationClub
            key={cardCollection.slug}
            ref={clubRefCallback}
            index={index}
            cardCollection={cardCollection}
            newCards={sortedNewCards}
            animatingCardIndex={animatingCardIndex}
            onCardAnimationEnd={() => {
              if (animatingCardIndex === undefined) {
                return;
              }
              if (animatingCardIndex === sortedNewCards.length - 1) {
                onAnimationEnd();
                return;
              }
              const isNextCardInSameCollection =
                sortedNewCards.at(animatingCardIndex + 1)?.anyTeam.slug ===
                sortedNewCards.at(animatingCardIndex)?.anyTeam.slug;

              if (!isNextCardInSameCollection) {
                scrollTo(animatingCardIndex + 1);
                setAnimatingCardIndexAfterScroll(animatingCardIndex + 1);
              } else {
                setAnimatingCardIndex(animatingCardIndex + 1);
              }
            }}
          />
        ))}
      </CollectionsWrapper>
    </Root>
  );
};

CollectionAnimationCompetition.fragments = {
  anyCardInterface: gql`
    fragment CollectionAnimationCompetition_anyCardInterface on AnyCardInterface {
      slug
      seasonYear
      anyTeam {
        slug
        shortName
        ... on Club {
          slug
          domesticLeague {
            slug
          }
        }
      }
    }
    ${CollectionAnimationHeader.fragments.competition}
  ` as TypedDocumentNode<CollectionAnimationCompetition_anyCardInterface>,
  competition: gql`
    fragment CollectionAnimationCompetition_competition on Competition {
      slug
      ...CollectionAnimationHeader_competition
    }
    ${CollectionAnimationHeader.fragments.competition}
  ` as TypedDocumentNode<CollectionAnimationCompetition_competition>,
};
